/**
 * Contour preview API client — SF-CONTOUR-105 / PL-CONTOUR-101.
 *
 * Typed wrappers around `/api/v1/contour/previews`: queues (and polls) the
 * server-side background-removal + contour-detection pipeline for an
 * already-uploaded artwork file. This is the "server preview" the upload
 * zone shows in place of the browser WASM pipeline above the admin-
 * configured decode threshold (`browserDecodeMaxSizeBytes`) — see
 * `.claude/rules/frontend-data-flow.md` for why this file, not a capsule or
 * a slice, owns the network calls.
 *
 * Works for both a signed-in customer and a guest (ownership by the
 * caller's session — same binding as guest artwork uploads); no token to
 * pass beyond the usual `apiClient` credentials.
 */
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  ContourBackgroundRemovalMode,
  ContourPreview,
  ContourPreviewWireDTO,
} from '@dfq/shared/types/contourPreview';

import { apiClient } from './client';
import { throwAsApiError, unwrap } from './_envelope';

function fromWire(wire: ContourPreviewWireDTO): ContourPreview {
  return {
    id: wire.id,
    artworkFileId: wire.artworkFileId,
    status: wire.status,
    widthInches: wire.widthInches,
    heightInches: wire.heightInches,
    errorMessage: wire.errorMessage,
    severity: wire.severity,
    attempts: wire.attempts,
    retryable: wire.retryable,
    backgroundRemovalMode: wire.backgroundRemovalMode ?? null,
    rawContourPathJson: wire.rawContourPathJson ?? null,
    originalWidthPx: wire.originalWidthPx ?? null,
    originalHeightPx: wire.originalHeightPx ?? null,
    targetDpi: wire.targetDpi ?? null,
    previewImageUrl: wire.previewImageUrl ?? null,
    cutPathUrl: wire.cutPathUrl ?? null,
  };
}

/**
 * `POST /api/v1/contour/previews` — queue a preview for an uploaded artwork file.
 * The server dedupes on artwork + mode + owner, so re-posting an unchanged pair is cheap.
 */
export async function createContourPreview(
  artworkFileId: number,
  backgroundRemovalMode?: ContourBackgroundRemovalMode,
): Promise<ContourPreview> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ contour_preview: ContourPreviewWireDTO }>>(
      '/contour/previews',
      {
        artwork_file_id: artworkFileId,
        ...(backgroundRemovalMode !== undefined ? { background_removal_mode: backgroundRemovalMode } : {}),
      },
    );
    return fromWire(unwrap(res.data, res.status).contour_preview);
  } catch (error) {
    throwAsApiError(error);
  }
}

/** `GET /api/v1/contour/previews/{id}` — poll the current state (with presigned URLs once succeeded). */
export async function getContourPreview(previewId: number): Promise<ContourPreview> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ contour_preview: ContourPreviewWireDTO }>>(
      `/contour/previews/${previewId}`,
    );
    return fromWire(unwrap(res.data, res.status).contour_preview);
  } catch (error) {
    throwAsApiError(error);
  }
}

/** `POST /api/v1/contour/previews/{id}/retry` — re-queue a `failed` preview. */
export async function retryContourPreview(previewId: number): Promise<ContourPreview> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ contour_preview: ContourPreviewWireDTO }>>(
      `/contour/previews/${previewId}/retry`,
    );
    return fromWire(unwrap(res.data, res.status).contour_preview);
  } catch (error) {
    throwAsApiError(error);
  }
}
